<template>
  <div class="container-fluid full-width-page-wrapper">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h3 class="font-weight-bold page-title mb-0">{{ pageTitle }}</h3>
      <a
        v-if="revision"
        class="btn btn-outline-primary"
        :href="pdfLink"
        target="_blank"
        rel="noopener"
      >
        Print PDF
      </a>
    </div>

    <b-alert v-if="forbidden" show variant="warning">
      You do not have permission to validate technical areas revisions.
    </b-alert>

    <ifac-loader v-else-if="busy" />

    <div v-else-if="!revision && !pendingRevisions.length">
      <b-alert show variant="info">
        No technical areas revision is currently awaiting validation.
      </b-alert>
    </div>

    <div v-else>
      <b-button-group v-if="pendingRevisions.length > 1" class="mb-3">
        <b-button
          v-for="item in pendingRevisions"
          :key="item.id"
          :variant="item.id === selectedRevisionId ? 'primary' : 'outline-primary'"
          @click="selectRevision(item.id)"
        >
          {{ revisionTabLabel(item) }}
        </b-button>
      </b-button-group>

      <div v-if="revision">
        <b-alert show :variant="statusVariant">
          {{ revisionTypeLabel }} — {{ revision.status.replace(/_/g, ' ') }}
        </b-alert>

        <ifac-technical-areas-accordion :coordinating-committees="previewCommittees" />

        <div v-if="revision.isAwaitingValidation && canVote" class="mt-4">
          <b-button class="mr-2" variant="success" :disabled="busy" @click="vote('approved')">
            Approve
          </b-button>
          <b-button variant="danger" :disabled="busy" @click="vote('disapproved')">
            Disapprove
          </b-button>
        </div>

        <b-card v-if="revision.votes?.length" class="mt-4" header="Validation votes">
          <ul class="mb-0">
            <li v-for="vote in revision.votes" :key="vote.id">
              {{ vote.affiliateName || 'Unknown' }} — {{ vote.positionName }} — {{ vote.response }}
            </li>
          </ul>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import { IfacLoader, IfacTechnicalAreasAccordion } from '@ifac/ui';
import Revisions from '@/services/Api/TechnicalAreas/Revisions';

export default {
  name: 'TechnicalAreasValidate',
  components: {
    IfacLoader,
    IfacTechnicalAreasAccordion,
  },
  data() {
    return {
      busy: true,
      forbidden: false,
      revision: null,
      pendingRevisions: [],
      selectedRevisionId: null,
    };
  },
  computed: {
    pageTitle() {
      if (!this.revision) {
        return 'Validate Technical Areas';
      }

      return this.revision.type === 'keywords'
        ? 'Validate Technical Areas Keywords'
        : 'Validate Technical Areas Structure';
    },
    revisionTypeLabel() {
      return this.revision?.type === 'keywords' ? 'Keywords review' : 'Structure review';
    },
    previewCommittees() {
      const committees = this.revision?.snapshot?.coordinatingCommittees || [];

      return committees
        .filter((committee) => !committee.deleted)
        .map((committee) => ({
          ...committee,
          urls: committee.urls || [],
          technicalCommittees: (committee.technicalCommittees || [])
            .filter((tc) => !tc.deleted)
            .map((tc) => ({
              ...tc,
              ccNumber: committee.number,
              fullNumber: `${committee.number}.${tc.number}`,
              urls: tc.urls || [],
              keywords: (tc.keywords || []).filter((keyword) => !keyword.deleted),
              conferencesMainSponsor: tc.conferencesMainSponsor || [],
              conferencesCoSponsor: tc.conferencesCoSponsor || [],
            }))
            .sort((a, b) => Number(a.number) - Number(b.number)),
        }))
        .sort((a, b) => Number(a.number) - Number(b.number));
    },
    pdfLink() {
      if (!this.revision) {
        return '#';
      }

      const token = this.$store.state.auth?.tokens?.token;

      return Revisions.pdfUrl(this.revision.id, token);
    },
    statusVariant() {
      if (this.revision?.isValidated) {
        return 'success';
      }

      if (this.revision?.isAwaitingValidation) {
        return 'warning';
      }

      return 'info';
    },
    canVote() {
      const userId = this.$store.state.auth?.user?.id;

      if (!userId) {
        return false;
      }

      return this.revision?.votes?.some(
        (vote) => vote.isPending && vote.affiliateId === userId,
      ) ?? false;
    },
  },
  async created() {
    await this.loadRevision();
  },
  methods: {
    revisionTabLabel(item) {
      return item.type === 'keywords' ? 'Keywords' : 'Structure';
    },
    async loadRevision() {
      this.busy = true;
      this.forbidden = false;

      try {
        const [structureResponse, keywordsResponse] = await Promise.all([
          Revisions.activeStructure(),
          Revisions.activeKeywords(),
        ]);

        this.pendingRevisions = [structureResponse.data.data, keywordsResponse.data.data]
          .filter((item) => item && ['validation_requested', 'validated'].includes(item.status));

        const selected = this.pendingRevisions.find((item) => item.id === this.selectedRevisionId)
          ?? this.pendingRevisions[0]
          ?? null;

        if (selected) {
          this.selectedRevisionId = selected.id;
          const detail = await Revisions.show(selected.id);
          this.revision = detail.data.data;
        } else {
          this.revision = null;
          this.selectedRevisionId = null;
        }
      } catch (error) {
        if (error.response?.status === 403) {
          this.forbidden = true;
        } else {
          throw error;
        }
      } finally {
        this.busy = false;
      }
    },
    async selectRevision(revisionId) {
      this.selectedRevisionId = revisionId;
      await this.loadRevision();
    },
    async vote(response) {
      this.busy = true;

      try {
        const result = await Revisions.recordVote(this.revision.id, response);
        this.revision = result.data.data;
        this.$snack.success({ text: `Vote recorded: ${response}.` });

        if (this.revision.status === 'draft') {
          this.$snack.show({ text: 'Revision returned to draft after a disapproval.' });
          await this.loadRevision();
        }
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
